import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PRESETS, SHAPES, lookFor } from '../components/MembraneField';

// The ghosts field behind every page: one field for the whole visit, so its
// preset and edits live here rather than in the hero. Kept for the visit (this
// tab), so a reload finds the same preset and edits too.
const FIELD_STORE = 'hero-field';

const loadField = () => {
  const fresh = { preset: 0, look: lookFor(PRESETS[0]), edited: false };
  try {
    const kept = JSON.parse(sessionStorage.getItem(FIELD_STORE));
    if (!kept || !PRESETS[kept.preset] || !kept.look) return fresh;
    const look = { ...lookFor(PRESETS[kept.preset]), ...kept.look };
    if (!SHAPES.some(([name]) => name === look.shape)) look.shape = 'rings';
    return { preset: kept.preset, look, edited: Boolean(kept.edited) };
  } catch {
    return fresh;
  }
};

const FieldContext = createContext(null);

export const FieldProvider = ({ children }) => {
  const [kept] = useState(loadField);
  const [preset, setPreset] = useState(kept.preset);
  const [look, setLook] = useState(kept.look);
  const [edited, setEdited] = useState(kept.edited);
  const [editing, setEditing] = useState(false);
  // Whether ghosts has drawn (or given up): the site holds its arrival until then.
  const [lit, setLit] = useState(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(FIELD_STORE, JSON.stringify({ preset, look, edited }));
    } catch {}
  }, [preset, look, edited]);

  const pick = useCallback(
    (index) => {
      setPreset(index);
      setEdited(false);
      // A preset brings its own layers and filters. Shape and colour carry over
      // once the visitor has changed them, unless the preset has its own.
      setLook((now) => {
        const target = PRESETS[index];
        const was = lookFor(PRESETS[preset]);
        let next = { ...lookFor(target), word: now.word };
        if (now.shape !== was.shape && !target.shape) next = { ...next, shape: now.shape };
        const recoloured = now.colour !== was.colour || now.hue !== was.hue;
        if (recoloured && !target.colour) next = { ...next, colour: now.colour, hue: now.hue };
        return next;
      });
    },
    [preset]
  );

  const value = useMemo(
    () => ({
      preset,
      look,
      edited,
      editing,
      lit,
      pick,
      step: (by) => pick((preset + by + PRESETS.length) % PRESETS.length),
      edit: (next) => {
        setLook(next);
        setEdited(true);
      },
      reset: () => {
        setLook(lookFor(PRESETS[preset]));
        setEdited(false);
      },
      setEditing,
      light: () => setLit(true),
    }),
    [preset, look, edited, editing, lit, pick]
  );

  return <FieldContext.Provider value={value}>{children}</FieldContext.Provider>;
};

export const useField = () => useContext(FieldContext);
